import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "wouter";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { StatusBadge } from "@/components/StatusBadge";
import type { IncidentDto, IncidentStatus } from "@/lib/types";

interface IncidentList {
  incidents: IncidentDto[];
  pagination: { limit: number; offset: number; count: number };
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | "ALL">("ALL");

  const { data, isLoading, refetch, isFetching } = useQuery<IncidentList>({
    queryKey: ["incidents", statusFilter],
    queryFn: () => {
      const qs = statusFilter === "ALL" ? "" : `?status=${statusFilter}`;
      return api<IncidentList>(`/incidents${qs}`);
    },
    refetchInterval: 5000,
  });

  const incidents = data?.incidents ?? [];

  const counts = {
    draft: incidents.filter((i) => i.status === "DRAFT").length,
    active: incidents.filter((i) => i.status === "ACTIVE").length,
    acknowledged: incidents.filter((i) => i.status === "ACKNOWLEDGED").length,
    resolved: incidents.filter((i) => i.status === "RESOLVED").length,
  };

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">{t("dashboard.title")}</h1>
          <p className="text-xs text-slate-400">
            {t("dashboard.welcome")}, {user?.fullName ?? "—"}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          data-testid="dashboard-refresh"
          className="text-xs text-slate-300 border border-slate-700 rounded px-3 py-1 hover:border-teal-500 inline-flex items-center gap-1"
        >
          <RefreshCw className={`size-3 ${isFetching ? "animate-spin" : ""}`} />
          {t("dashboard.refresh")}
        </button>
      </header>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          label={t("dashboard.draftIncidents")}
          value={counts.draft}
          tone="amber"
          testId="kpi-draft"
        />
        <KpiCard
          label={t("dashboard.activeAlerts")}
          value={counts.active}
          tone="rose"
          testId="kpi-active"
        />
        <KpiCard
          label={t("dashboard.acknowledged")}
          value={counts.acknowledged}
          tone="blue"
          testId="kpi-ack"
        />
        <KpiCard
          label={t("dashboard.resolvedToday")}
          value={counts.resolved}
          tone="emerald"
          testId="kpi-resolved"
        />
      </section>

      <section className="rounded-lg border border-slate-800 bg-slate-900/40">
        <div className="px-4 py-2 flex items-center gap-3 border-b border-slate-800">
          <span className="text-xs text-slate-400 uppercase tracking-wide">
            {t("dashboard.filterStatus")}
          </span>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as IncidentStatus | "ALL")
            }
            data-testid="status-filter"
            className="bg-slate-950 border border-slate-700 rounded text-xs px-2 py-1"
          >
            <option value="ALL">{t("dashboard.filterAll")}</option>
            <option value="DRAFT">{t("statusBadge.DRAFT")}</option>
            <option value="ACTIVE">{t("statusBadge.ACTIVE")}</option>
            <option value="ACKNOWLEDGED">{t("statusBadge.ACKNOWLEDGED")}</option>
            <option value="INVESTIGATING">{t("statusBadge.INVESTIGATING")}</option>
            <option value="RESOLVED">{t("statusBadge.RESOLVED")}</option>
          </select>
        </div>

        {isLoading && (
          <div className="p-6 text-sm text-slate-500" data-testid="dashboard-loading">
            {t("dashboard.loading")}
          </div>
        )}
        {!isLoading && incidents.length === 0 && (
          <div className="p-6 text-sm text-slate-500 italic" data-testid="dashboard-empty">
            {t("dashboard.noIncidents")}
          </div>
        )}
        {!isLoading && incidents.length > 0 && (
          <ul className="divide-y divide-slate-800" data-testid="incident-list">
            {incidents.map((incident) => (
              <li key={incident.id} data-testid={`incident-row-${incident.id}`}>
                <Link
                  href={`/incidents/${incident.id}`}
                  className="block px-4 py-3 hover:bg-slate-800/40 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <StatusBadge status={incident.status} />
                      <span className="text-sm font-mono text-slate-200">
                        {incident.eventType}
                      </span>
                      <span className="text-xs text-slate-500">
                        {incident.siteId} / {incident.segmentId}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-slate-300">
                        {Math.round(incident.confidence * 100)}%
                      </span>
                      <span
                        className={`text-xs font-mono ${
                          incident.sla.colour === "red"
                            ? "text-rose-400"
                            : incident.sla.colour === "amber"
                            ? "text-amber-300"
                            : "text-slate-500"
                        }`}
                      >
                        SLA {incident.sla.elapsedSeconds}s
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone,
  testId,
}: {
  label: string;
  value: number;
  tone: "amber" | "rose" | "blue" | "emerald";
  testId: string;
}) {
  const toneClass: Record<string, string> = {
    amber: "border-amber-800/40 text-amber-300",
    rose: "border-rose-800/40 text-rose-300",
    blue: "border-blue-800/40 text-blue-300",
    emerald: "border-emerald-800/40 text-emerald-300",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-lg border ${toneClass[tone]} bg-slate-900/40 px-3 py-2`}
    >
      <div className="text-2xl font-mono">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}
